import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, ArrowRight, ChevronLeft, ChevronRight, Clock, Phone, Mail, Facebook, Instagram, Linkedin, Youtube, Twitter, Minus, Maximize2, Map as MapIcon, Link2, MessageCircle, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { goTo } from "@/threedvista/bridge";
import { embedUrl, PlanImage } from "@/threedvista/TdvExtras";

export const SOCIALS = [["facebook", "Facebook", Facebook], ["instagram", "Instagram", Instagram], ["linkedin", "LinkedIn", Linkedin], ["x", "X", Twitter], ["youtube", "YouTube", Youtube]];

function VideoDialog({ url, open, onClose }) {
  const emb = embedUrl(url);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl bg-black border-white/10 p-0 overflow-hidden rounded-3xl" data-testid="tdv-hero-video-dialog">
        <DialogTitle className="sr-only">Video</DialogTitle>
        {open && (emb ? <iframe title="Video" src={`${emb}&autoplay=1`} className="w-full aspect-video border-0" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
          : <video src={url} controls autoPlay playsInline className="w-full" />)}
      </DialogContent>
    </Dialog>
  );
}

export function HeroStart({ tour, b, list, base, tdv, onStart }) {
  const thumbs = list.filter((s) => s.thumb).slice(0, 12);
  const [i, setI] = useState(0);
  const [video, setVideo] = useState(false);
  useEffect(() => {
    if (thumbs.length < 2 || video) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % thumbs.length), 6000);
    return () => clearInterval(t);
  }, [thumbs.length, video]);
  useEffect(() => { if (tdv && thumbs[i]) goTo(tdv, thumbs[i].index); }, [i, tdv]); // eslint-disable-line react-hooks/exhaustive-deps
  const src = (s) => { try { return new URL(s.thumb, base).href; } catch { return ""; } };
  return (
    <div className="absolute inset-0 z-50 text-white" data-testid="tdv-hero-start">
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/45 to-slate-950/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
      <div className="relative h-full flex flex-col justify-end lg:justify-center px-6 sm:px-12 lg:px-24 pb-40 lg:pb-0 max-w-3xl">
        <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-white/85 rise" data-testid="tdv-hero-kicker">{tour.hero_kicker || `Welcome to ${b.name}`}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4 rise" style={{ animationDelay: "80ms" }} data-testid="tdv-hero-title">{tour.hero_title || tour.name}</h1>
        {(tour.hero_text || tour.description) && <p className="text-sm sm:text-base text-white/80 mt-5 max-w-xl rise" style={{ animationDelay: "160ms" }} data-testid="tdv-hero-text">{tour.hero_text || tour.description}</p>}
        <div className="flex flex-wrap items-center gap-6 mt-8 rise" style={{ animationDelay: "240ms" }}>
          <button onClick={onStart} className="h-12 px-7 bg-bp text-white text-sm font-semibold inline-flex items-center gap-3 rounded-[var(--sk-radius,0.75rem)] hover:brightness-110 transition-[filter]" data-testid="tdv-hero-start-button">{tour.hero_cta || "Start the tour"}<ArrowRight className="w-4 h-4" /></button>
          {tour.hero_video_url && <button onClick={() => setVideo(true)} className="inline-flex items-center gap-3 text-sm font-semibold hover:opacity-80" data-testid="tdv-hero-video-button"><span className="w-10 h-10 rounded-full border border-white/50 grid place-items-center"><Play className="w-4 h-4 fill-white" /></span>Watch video</button>}
        </div>
      </div>
      {thumbs.length > 1 && (
        <div className="absolute right-4 sm:right-12 lg:right-24 bottom-8 left-4 sm:left-auto sm:w-[min(46rem,50vw)]" data-testid="tdv-hero-thumbs">
          <div className="flex gap-3 overflow-x-auto pb-3">{thumbs.map((s, j) => (
            <button key={s.id} onClick={() => setI(j)} className={`shrink-0 w-28 sm:w-36 aspect-[4/3] overflow-hidden rounded-lg transition-[transform,box-shadow] duration-300 ${j === i ? "ring-4 ring-white/80 scale-105" : "opacity-75 hover:opacity-100"}`} data-testid={`tdv-hero-thumb-${j}`}>
              <img src={src(s)} alt={s.label} className="w-full h-full object-cover" />
            </button>
          ))}</div>
          <div className="h-0.5 bg-white/25 relative"><div className="absolute inset-y-0 left-0 bg-bp transition-[width] duration-500" style={{ width: `${((i + 1) / thumbs.length) * 100}%` }} /></div>
        </div>
      )}
      <VideoDialog url={tour.hero_video_url} open={video} onClose={() => setVideo(false)} />
    </div>
  );
}

export function InfoStrip({ tour }) {
  const so = tour.socials || {};
  return (
    <div className="absolute z-20 top-2 left-3 right-3 h-8 px-4 rounded-full bg-slate-950/45 backdrop-blur-md flex items-center gap-4 text-xs text-white" data-testid="tdv-info-strip">
      {tour.info_hours && <span className="inline-flex items-center gap-1.5 truncate" data-testid="tdv-info-hours"><Clock className="w-3.5 h-3.5 shrink-0" />{tour.info_hours}</span>}
      <span className="ml-auto" />
      {tour.info_phone && <a href={`tel:${tour.info_phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1.5 hover:underline" data-testid="tdv-info-phone"><Phone className="w-3.5 h-3.5" /><span className="hidden sm:inline">{tour.info_phone}</span></a>}
      {tour.info_email && <a href={`mailto:${tour.info_email}`} className="inline-flex items-center gap-1.5 hover:underline" data-testid="tdv-info-email"><Mail className="w-3.5 h-3.5" /><span className="hidden sm:inline">{tour.info_email}</span></a>}
      {SOCIALS.filter(([k]) => so[k]).map(([k, l, I]) => <a key={k} href={so[k]} target="_blank" rel="noopener noreferrer" title={l} className="hover:opacity-70" data-testid={`tdv-info-social-${k}`}><I className="w-3.5 h-3.5" /></a>)}
    </div>
  );
}

export function MiniPlan({ tour, labels, current, onPick, onExpand, top }) {
  const plans = tour.floorplans || [];
  const [min, setMin] = useState(window.innerWidth < 768);
  const plan = plans.find((p) => p.id === tour.plan_pins?.[current]?.floor) || plans[0];
  if (!plan) return null;
  if (min) return (
    <button onClick={() => setMin(false)} className={`absolute z-20 right-3 ${top} h-10 px-4 rounded-full glass-dark text-white text-xs font-semibold inline-flex items-center gap-2`} data-testid="tdv-mini-plan-open"><MapIcon className="w-4 h-4" />Floor plan</button>
  );
  return (
    <div className={`absolute z-20 right-3 ${top} w-64 rounded-2xl glass-dark text-white p-3 rise`} data-testid="tdv-mini-plan">
      <div className="flex items-center mb-2"><p className="text-[11px] font-semibold tracking-widest uppercase flex-1 truncate">{plan.name || "Floor plan"}</p>
        <button onClick={() => setMin(true)} title="Minimise" className="w-7 h-7 rounded-full hover:bg-white/10 grid place-items-center" data-testid="tdv-mini-plan-min"><Minus className="w-4 h-4" /></button>
        <button onClick={onExpand} title="Enlarge" className="w-7 h-7 rounded-full hover:bg-white/10 grid place-items-center" data-testid="tdv-mini-plan-expand"><Maximize2 className="w-3.5 h-3.5" /></button></div>
      <div className="grid place-items-center"><PlanImage plan={plan} pins={tour.plan_pins} labels={labels} current={current} onPick={onPick} imgClass="max-h-44" /></div>
    </div>
  );
}

export function SceneNav({ list, current, onGo, shift }) {
  const i = list.findIndex((s) => s.index === current);
  if (i < 0 || list.length < 2) return null;
  const go = (d) => onGo(list[(i + d + list.length) % list.length].index);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div className={`absolute z-20 bottom-4 left-4 ${shift ? "md:left-[17.5rem]" : ""} max-w-[calc(100%-6rem)] text-white [text-shadow:0_2px_10px_rgba(0,0,0,.6)] transition-[left] duration-300`} data-testid="tdv-scene-nav">
      <p className="text-[11px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-white/85" data-testid="tdv-scene-nav-count">You are here · {pad(i + 1)} / {pad(list.length)}</p>
      <div className="flex items-center gap-3 mt-1">
        <p className="font-serif text-2xl sm:text-4xl truncate" data-testid="tdv-scene-nav-name">{list[i].label}</p>
        <button onClick={() => go(-1)} title="Previous scene" className="w-10 h-10 shrink-0 rounded-full glass-dark grid place-items-center hover:scale-105 transition-transform" data-testid="tdv-scene-prev"><ChevronLeft className="w-5 h-5" /></button>
        <button onClick={() => go(1)} title="Next scene" className="w-10 h-10 shrink-0 rounded-full glass-dark grid place-items-center hover:scale-105 transition-transform" data-testid="tdv-scene-next"><ChevronRight className="w-5 h-5" /></button>
      </div>
    </div>
  );
}

export function ShareDialog({ open, onClose, title }) {
  const url = window.location.href.split("?")[0];
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  const links = [
    ["WhatsApp", MessageCircle, `https://wa.me/?text=${t}%20${u}`],
    ["Email", Mail, `mailto:?subject=${t}&body=${u}`],
    ["Facebook", Facebook, `https://www.facebook.com/sharer/sharer.php?u=${u}`],
    ["LinkedIn", Linkedin, `https://www.linkedin.com/sharing/share-offsite/?url=${u}`],
    ["X", Twitter, `https://twitter.com/intent/tweet?url=${u}&text=${t}`],
  ];
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success("Link copied"), () => toast.error("Couldn't copy the link"));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md bg-slate-950/95 text-white border-white/10 rounded-3xl overflow-hidden [&>*]:min-w-0" data-testid="tdv-share-dialog">
        <DialogTitle className="font-serif text-2xl">Share this tour</DialogTitle>
        <button onClick={copy} className="w-full min-w-0 flex items-center gap-3 rounded-2xl bg-white/10 hover:bg-white/15 px-4 h-12 text-sm text-left transition-colors" data-testid="tdv-share-copy"><Link2 className="w-4 h-4 shrink-0" /><span className="truncate flex-1 min-w-0 text-white/80">{url}</span><span className="font-semibold shrink-0">Copy</span></button>
        <div className="grid grid-cols-5 gap-1 sm:gap-2">{links.map(([l, I, href]) => (
          <a key={l} href={href} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1.5 text-[11px] text-white/80 hover:text-white" data-testid={`tdv-share-${l.toLowerCase()}`}>
            <span className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 grid place-items-center hover:bg-white/20 transition-colors"><I className="w-5 h-5" /></span>{l}</a>
        ))}</div>
      </DialogContent>
    </Dialog>
  );
}

export function ContactTab({ label, onClick }) {
  return (
    <button onClick={onClick} className="absolute z-10 left-0 top-1/2 -translate-y-1/2 bg-bp text-white text-sm font-semibold px-3 py-5 rounded-r-xl shadow-xl inline-flex flex-col items-center gap-2 hover:pl-4 transition-[padding]" data-testid="tdv-contact-tab">
      <ArrowUpRight className="w-4 h-4" /><span className="[writing-mode:vertical-rl] rotate-180">{label || "Contact us"}</span>
    </button>
  );
}
